import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  Paper,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import { MockBadge } from '../../../../Components/Common/MockBadge';
import {
  getDocumentsByOrganization,
  getDocumentTypeByCode
} from '../../../../mockData/orgDocIndex';

const steps = ['Review Requirements', 'Select Documents', 'Confirm & Pay'];

/**
 * Request Tax Service Dialog
 * Multi-step dialog for requesting a tax service
 *
 * Steps:
 * 1. Review service requirements
 * 2. Select/upload required documents
 * 3. Confirm and pay
 *
 * @mock Uses mock data during UI-First phase
 */
const RequestTaxServiceDialog = ({
  open,
  onClose,
  service,
  taxProfile,
  organizationId,
  onSubmit
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState({});
  const [taxPeriod, setTaxPeriod] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);

  // Reset when dialog opens
  useEffect(() => {
    if (open && service) {
      setActiveStep(0);
      setSelectedDocuments({});
      setTaxPeriod(getCurrentTaxPeriod(service));
      setNotes('');

      // Get available documents
      const docs = getDocumentsByOrganization(organizationId);
      setAvailableDocuments(docs);
    }
  }, [open, service, organizationId]);

  // Get current tax period based on service type
  const getCurrentTaxPeriod = (svc) => {
    if (!svc) return '';
    if (svc.filingPeriod === 'QUARTERLY') {
      const now = new Date();
      const quarter = Math.ceil((now.getMonth() + 1) / 3);
      return `Q${quarter} ${now.getFullYear()}`;
    }
    if (svc.filingPeriod === 'ANNUAL') {
      return new Date().getFullYear().toString();
    }
    return '';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Handle document selection
  const handleDocumentSelect = (docTypeCode, documentId) => {
    setSelectedDocuments(prev => ({
      ...prev,
      [docTypeCode]: documentId
    }));
  };

  // Check if all required documents are selected
  const allDocumentsSelected = () => {
    if (!service) return false;
    return service.requiredDocuments.every(docCode =>
      selectedDocuments[docCode] || getDocumentForType(docCode)
    );
  };

  // Get existing document for a type
  const getDocumentForType = (docTypeCode) => {
    return availableDocuments.find(doc =>
      doc.documentTypeCode === docTypeCode &&
      doc.status === 'ACTIVE'
    );
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Handle submit
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Create request object
      const request = {
        id: `req-${Date.now()}`,
        serviceId: service.id,
        serviceCode: service.code,
        serviceName: service.name,
        organizationId,
        status: 'IN_PROGRESS',
        requestedAt: new Date().toISOString(),
        completedAt: null,
        totalCharge: service.totalCharge,
        paymentStatus: 'PAID',
        outputDocumentId: null,
        notes,
        taxPeriod,
        selectedDocuments,
        _isMock: true
      };

      onSubmit(request);
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) return null;

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Service Summary */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {service.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {service.longDescription}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Pricing */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Estimated Processing Time
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {service.estimatedDays} business days
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Filing Deadline
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {service.filingDeadline || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Required Documents */}
            <Typography variant="subtitle2" gutterBottom>
              Required Documents
            </Typography>
            <List dense>
              {service.requiredDocuments.map((docCode) => {
                const existingDoc = getDocumentForType(docCode);
                const docType = getDocumentTypeByCode(docCode);
                return (
                  <ListItem key={docCode}>
                    <ListItemIcon>
                      {existingDoc ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <DescriptionIcon color="warning" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={docType?.name || docCode}
                      secondary={existingDoc
                        ? `Available: ${existingDoc.fileName}`
                        : 'Not uploaded - will need to provide'
                      }
                    />
                    {existingDoc ? (
                      <Chip label="Ready" size="small" color="success" />
                    ) : (
                      <Chip label="Required" size="small" color="warning" variant="outlined" />
                    )}
                  </ListItem>
                );
              })}
            </List>

            {/* Output Documents */}
            {service.outputDocuments?.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Output Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upon completion, you will receive: {service.outputDocuments.join(', ')}
                </Typography>
              </>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            {/* Tax Period Selection */}
            {(service.filingPeriod === 'QUARTERLY' || service.filingPeriod === 'ANNUAL') && (
              <TextField
                select
                label="Tax Period"
                value={taxPeriod}
                onChange={(e) => setTaxPeriod(e.target.value)}
                fullWidth
                sx={{ mb: 3 }}
              >
                {service.filingPeriod === 'QUARTERLY' && (
                  ['Q1 2025', 'Q4 2024', 'Q3 2024', 'Q2 2024'].map(period => (
                    <MenuItem key={period} value={period}>{period}</MenuItem>
                  ))
                )}
                {service.filingPeriod === 'ANNUAL' && (
                  ['2024', '2023'].map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))
                )}
              </TextField>
            )}

            {/* Document Selection */}
            <Typography variant="subtitle2" gutterBottom>
              Select Documents to Submit
            </Typography>
            <List>
              {service.requiredDocuments.map((docCode) => {
                const docType = getDocumentTypeByCode(docCode);
                const matchingDocs = availableDocuments.filter(
                  doc => doc.documentTypeCode === docCode
                );

                return (
                  <Paper key={docCode} variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      {docType?.name || docCode}
                    </Typography>

                    {matchingDocs.length > 0 ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={selectedDocuments[docCode] || matchingDocs[0]?.id || ''}
                        onChange={(e) => handleDocumentSelect(docCode, e.target.value)}
                      >
                        {matchingDocs.map(doc => (
                          <MenuItem key={doc.id} value={doc.id}>
                            {doc.fileName} ({doc.status})
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        No {docType?.name || docCode} found. Please upload this document first.
                      </Alert>
                    )}
                  </Paper>
                );
              })}
            </List>

            {/* Additional Notes */}
            <TextField
              label="Additional Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            {/* Order Summary */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Order Summary
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Service</Typography>
                <Typography variant="body2">{service.name}</Typography>
              </Box>

              {taxPeriod && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Tax Period</Typography>
                  <Typography variant="body2">{taxPeriod}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {service.govtCharge > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Government Fee</Typography>
                  <Typography variant="body2">{formatCurrency(service.govtCharge)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Service Fee</Typography>
                <Typography variant="body2">{formatCurrency(service.serviceCharge)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">VAT (5%)</Typography>
                <Typography variant="body2">{formatCurrency(service.taxOnServiceCharge)}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">Total</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatCurrency(service.totalCharge)}
                </Typography>
              </Box>
            </Paper>

            {/* Payment Method */}
            <Alert severity="info" icon={<PaymentIcon />}>
              <AlertTitle>Payment</AlertTitle>
              Amount will be deducted from your wallet balance. Current balance: {formatCurrency(50000)}
            </Alert>

            {/* Terms */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              By clicking "Submit Request", you agree to our terms of service and authorize the payment.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Request Tax Service
          <MockBadge inline />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {submitting ? 'Processing...' : `Pay ${formatCurrency(service.totalCharge)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequestTaxServiceDialog;
